import { atom, useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

/**
 * Per-workspace prompt history persisted in `localStorage`. Each entry is
 * pushed at successful generate-time so the user can recall their last
 * prompts from a popover anchored on the prompt panel (iter 37 in the
 * Photo Studio priority queue).
 *
 * Design choices (validated against legacy `usePromptHistory`):
 *
 *   - Storage key is namespaced **per workspace** so two workspaces in
 *     the same browser don't bleed prompts into each other. The legacy
 *     app keys by `aiProductImages.promptHistory.v1` with workspace
 *     baked into the bucket; we mirror the pattern verbatim via
 *     `studio.promptHistory.<workspaceId>`.
 *   - The atom holds a `{ [workspaceId]: PromptHistoryEntry[] }` map so
 *     we re-hydrate lazily when the workspace switches. Reading the
 *     atom never throws (corrupt JSON → empty list).
 *   - `pushPrompt` is the single mutation entry-point. It dedupes
 *     **consecutive** duplicates (per legacy) and trims to
 *     `MAX_PROMPT_HISTORY = 20`. Empty / whitespace-only prompts are
 *     ignored so the textarea's default state never spams storage.
 */

/** A single saved prompt with its insertion timestamp (ms-since-epoch). */
export type PromptHistoryEntry = {
  prompt: string;
  /** ms since epoch — drives the relative-time label ("3 min ago"). */
  createdAt: number;
};

/** Per-workspace cap. Legacy uses 20 across all modes; we mirror it. */
export const MAX_PROMPT_HISTORY = 20;

/** localStorage key prefix; the workspaceId is appended at runtime. */
export const PROMPT_HISTORY_KEY_PREFIX = 'studio.promptHistory.';

/** Shape of the in-memory atom value. */
type PromptHistoryMap = Record<string, PromptHistoryEntry[]>;

function storageKey(workspaceId: string): string {
  return `${PROMPT_HISTORY_KEY_PREFIX}${workspaceId}`;
}

/**
 * Read a workspace's saved entries from `localStorage`. Returns `[]` on
 * any failure (missing key, JSON parse error, malformed value).
 */
export function readPromptHistory(workspaceId: string): PromptHistoryEntry[] {
  if (!workspaceId) return [];
  try {
    if (typeof window === 'undefined') return [];
    const raw = window.localStorage.getItem(storageKey(workspaceId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Type-guard each entry; drop anything malformed so a stale schema
    // can't bork the popover.
    const valid = parsed.filter(
      (e): e is PromptHistoryEntry =>
        typeof e === 'object' &&
        e !== null &&
        'prompt' in e &&
        typeof (e as { prompt: unknown }).prompt === 'string' &&
        'createdAt' in e &&
        typeof (e as { createdAt: unknown }).createdAt === 'number',
    );
    // Strip consecutive duplicates that may have been written by an older
    // version of the code (pushPromptEntry guards against them on write, but
    // storage written before that fix may still contain them).
    const deduped = valid.reduce<PromptHistoryEntry[]>((acc, entry) => {
      if (acc.length > 0 && acc[acc.length - 1]!.prompt === entry.prompt) return acc;
      acc.push(entry);
      return acc;
    }, []);
    return deduped.slice(0, MAX_PROMPT_HISTORY);
  } catch {
    return [];
  }
}

/**
 * Persist a workspace's entries. Swallows quota errors (storage full,
 * Safari private mode, etc.) — the in-memory atom value still reflects
 * the latest state for the current session.
 */
function writePromptHistory(workspaceId: string, entries: PromptHistoryEntry[]): void {
  if (!workspaceId) return;
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(workspaceId), JSON.stringify(entries));
  } catch {
    // Quota exceeded or storage disabled — no-op. The atom still holds
    // the value for this session.
  }
}

/**
 * In-memory cache of per-workspace prompt history. Defaults to an empty
 * map; `usePromptHistory` hydrates the active workspace from storage on
 * mount and after each push.
 */
export const promptHistoryAtom = atom<PromptHistoryMap>({});

/**
 * Pure helper to compute the next entries list given an existing list,
 * a new prompt, and a timestamp. Exported for unit-testing the dedupe
 * + trim invariants without booting React.
 */
export function pushPromptEntry(
  existing: PromptHistoryEntry[],
  prompt: string,
  now: number = Date.now(),
): PromptHistoryEntry[] {
  const trimmed = prompt.trim();
  if (trimmed.length === 0) return existing;
  // Dedupe consecutive duplicates: if the most-recent entry matches,
  // refresh its timestamp instead of pushing a new row.
  const head = existing[0];
  if (head && head.prompt === trimmed) {
    return [{ prompt: trimmed, createdAt: now }, ...existing.slice(1)].slice(0, MAX_PROMPT_HISTORY);
  }
  return [{ prompt: trimmed, createdAt: now }, ...existing].slice(0, MAX_PROMPT_HISTORY);
}

/**
 * Top-level mutation: push a prompt for the given workspace. Updates
 * the atom in-place and persists to `localStorage` in the same tick.
 *
 * Returns the updated list so call-sites can assert without re-reading.
 */
export function pushPrompt(
  setHistory: (updater: (prev: PromptHistoryMap) => PromptHistoryMap) => void,
  prompt: string,
  workspaceId: string,
  now: number = Date.now(),
): void {
  if (!workspaceId) return;
  setHistory((prev) => {
    const current = prev[workspaceId] ?? readPromptHistory(workspaceId);
    const next = pushPromptEntry(current, prompt, now);
    if (next === current) return prev;
    writePromptHistory(workspaceId, next);
    return { ...prev, [workspaceId]: next };
  });
}

/**
 * React hook that exposes the active workspace's prompt history list
 * plus a single `push(prompt)` mutator. Re-hydrates from storage when
 * the workspace id changes so cross-tab/workspace switches don't show
 * stale data.
 */
export function usePromptHistory(workspaceId: string | undefined): {
  history: PromptHistoryEntry[];
  push: (prompt: string) => void;
} {
  const [map, setMap] = useAtom(promptHistoryAtom);
  const wsKey = workspaceId ?? '';

  // Lazy hydration: read from localStorage the first time we see this
  // workspace this session.
  useEffect(() => {
    if (!wsKey) return;
    setMap((prev) => {
      if (wsKey in prev) return prev;
      return { ...prev, [wsKey]: readPromptHistory(wsKey) };
    });
  }, [wsKey, setMap]);

  const push = useCallback(
    (prompt: string) => {
      if (!wsKey) return;
      pushPrompt(setMap, prompt, wsKey);
    },
    [wsKey, setMap],
  );

  return {
    history: map[wsKey] ?? [],
    push,
  };
}

/**
 * Format a saved entry's age as a short relative string ("just now",
 * "3 min ago", "2 hr ago", "5 d ago"). Pure helper; exported so the
 * popover renders without a date-fns dependency.
 */
export function formatRelativeTime(createdAt: number, now: number = Date.now()): string {
  const diffMs = Math.max(0, now - createdAt);
  const sec = Math.floor(diffMs / 1000);
  if (sec < 30) return 'just now';
  if (sec < 60) return `${sec} sec ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  return `${day} d ago`;
}
